// Compact star display/picker. Always 0–5.
//
// Read-only mode: pass `value`, omit `onChange`.
// Picker mode: pass both. Click a star to set it; click the current value to
// clear back to 0.

import type { KeyboardEvent, MouseEvent } from "react";

export interface StarRatingProps {
  value: number;
  onChange?: (next: number) => void;
  size?: number;
}

export function StarRating({ value, onChange, size = 18 }: StarRatingProps) {
  const readonly = !onChange;
  const handle = (n: number, ev?: MouseEvent | KeyboardEvent) => {
    if (readonly) return;
    ev?.preventDefault();
    onChange!(value === n ? 0 : n);
  };
  return (
    <span
      className="stars"
      role={readonly ? (value > 0 ? "img" : undefined) : "radiogroup"}
      aria-label={
        readonly
          ? value > 0
            ? `${value} of 5 stars`
            : "unrated"
          : "stars"
      }
      style={{ fontSize: size }}
    >
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= value;
        return (
          <span
            key={n}
            role={readonly ? undefined : "radio"}
            aria-checked={readonly ? undefined : filled}
            tabIndex={readonly ? undefined : 0}
            onClick={(e) => handle(n, e)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") handle(n, e);
            }}
            style={{
              cursor: readonly ? "default" : "pointer",
              opacity: filled ? 1 : 0.35,
              userSelect: "none",
            }}
          >
            ★
          </span>
        );
      })}
    </span>
  );
}
